import React, { useState, useEffect } from 'react';
import { Search, Download, RefreshCw } from 'lucide-react';
import { ASSETS_API_URL, ASSET_TYPES, ASSET_STATUSES, REGIONS, STATUS_COLORS, CONDITION_COLORS } from '../../config/assets';

export const AssetHistory = ({ user }: { user: any }) => {
  const [assets, setAssets]           = useState<any[]>([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterType, setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res  = await fetch(ASSETS_API_URL);
      const data = await res.json();
      if (data.success) setAssets(data.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = assets.filter(a => {
    const d = (a.purchase_date || a.last_seen_date || '').slice(0,10);
    if (search && ![a.asset_id,a.asset_type,a.vehicle_registration,a.unit,a.region,a.assigned_officer,a.brand,a.model]
      .join(' ').toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType   && a.asset_type !== filterType)   return false;
    if (filterStatus && a.status     !== filterStatus) return false;
    if (filterRegion && a.region     !== filterRegion) return false;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo   && d > dateTo)   return false;
    return true;
  });

  const exportCSV = () => {
    const cols = ['asset_id','asset_type','vehicle_registration','region','unit',
      'status','condition','current_mileage','operational_hours','purchase_date',
      'purchase_cost','last_maintenance_date','last_inspection_date','inspection_result',
      'assigned_officer','gps_latitude','gps_longitude'];
    const rows = [cols.join(','), ...filtered.map(a =>
      cols.map(c => `"${String(a[c]||'').replace(/"/g,'""')}"`).join(','))];
    const el = document.createElement('a');
    el.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(rows.join('\n'));
    el.download = `nexus360_assets_${new Date().toISOString().slice(0,10)}.csv`;
    el.click();
  };

  const reset = () => { setSearch(''); setFilterType(''); setFilterStatus(''); setFilterRegion(''); setDateFrom(''); setDateTo(''); };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="rounded-xl px-4 py-3 flex flex-wrap items-center gap-4 border border-blue-900/40 bg-slate-800/80 shadow-lg">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-blue-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="ID, type, immatriculation, unité, officier..."
            className="w-full pl-8 pr-3 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-xs focus:outline-none focus:border-blue-500/60 placeholder:text-slate-400"
            style={{backgroundColor:'#334155', caretColor:'white'}}/>
        </div>
        <div className="flex items-center gap-2">
          
          <span className="text-xs font-semibold text-slate-400 whitespace-nowrap">Type:</span>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="px-2 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-xs focus:outline-none focus:border-blue-500/60 min-w-36">
            <option value="">Tous les types</option>
            {ASSET_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          
          <span className="text-xs font-semibold text-slate-400 whitespace-nowrap">Statut:</span>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="px-2 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-xs focus:outline-none focus:border-blue-500/60 min-w-36">
            <option value="">Tous les statuts</option>
            {ASSET_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          
          <span className="text-xs font-semibold text-slate-400 whitespace-nowrap">Région:</span>
          <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)}
            className="px-2 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-xs focus:outline-none focus:border-blue-500/60 min-w-36">
            <option value="">Toutes les régions</option>
            {REGIONS.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          
          <span className="text-xs font-semibold text-slate-400 whitespace-nowrap">Période:</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="px-2 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-xs focus:outline-none focus:border-blue-500/60" style={{colorScheme:'dark'}}/>
          <span className="text-blue-300 text-xs">–</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="px-2 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-xs focus:outline-none focus:border-blue-500/60" style={{colorScheme:'dark'}}/>
        </div>
        {(search||filterType||filterStatus||filterRegion||dateFrom||dateTo) && (
          <button onClick={reset} className="px-2 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs hover:bg-red-500/20 transition-colors whitespace-nowrap">
            ✕ Réinitialiser
          </button>
        )}
        <div className="flex-1"/>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 font-medium">{filtered.length}/{assets.length}</span>
          <button onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-blue-300 text-xs hover:bg-slate-600 transition-colors transition-colors whitespace-nowrap">
            <Download className="w-3.5 h-3.5"/> Export CSV
          </button>
          <button onClick={fetchData}
            className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-xs font-semibold transition-colors shadow-lg whitespace-nowrap">
            <RefreshCw className="w-3.5 h-3.5"/> Actualiser
          </button>
        </div>
      </div>

      {/* Asset cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"/>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-12 text-center">
          <p className="text-slate-300">Aucun actif trouvé.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 overflow-hidden shadow-lg">
          <div className="overflow-x-auto">
            <div className="overflow-y-auto max-h-[600px]">
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {[
                    'ID Actif','Type','Immat.','Marque / Modèle','N° Série','Catégorie','Année',
                    'Région','Unité','Ville','Commissariat',
                    'Statut','Condition','Opérationnel depuis',
                    'Kilom.','Heures','Carburant','Capacité (L)','Der. ravitaillement','Qté (L)',
                    'Der. maintenance','Coût maint.','Alerte moteur','Alerte huile','Usure freins (%)',
                    'Der. inspection','Proch. inspection','Inspecteur','Résultat inspection','Notes inspection',
                    'Date réparation','Type réparation','Description réparation','Coût réparation','Technicien','Prestataire','Durée (j)',
                    'Perte / Vol','Date perte','Réf. rapport',
                    'Déclassement','Date déclassement','Approuvé par',
                    'GPS','Der. localisation','Officier','Email','QR Code',
                    'Coût acquisition','Créé le','Notes',
                  ].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => {
                  const sc  = STATUS_COLORS[a.status]    || { color:'text-gray-400', bg:'bg-gray-500/10', border:'border-gray-500/30' };
                  const cc  = CONDITION_COLORS[a.condition] || 'text-gray-400';
                  const td = "px-3 py-2.5 whitespace-nowrap text-slate-300 text-xs";
                  return (
                    <tr key={a.asset_id} className={`border-b border-blue-900/30 hover:bg-slate-700/50 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                      <td className="px-3 py-2.5 font-mono text-blue-400 font-medium whitespace-nowrap">{a.asset_id}</td>
                      <td className={td}>{a.asset_type||'—'}</td>
                      <td className="px-3 py-2.5 font-mono text-white whitespace-nowrap">{a.vehicle_registration||'—'}</td>
                      <td className={td}>{a.brand||'—'} {a.model||''}</td>
                      <td className={td}>{a.serial_number||'—'}</td>
                      <td className={td}>{a.category||'—'}</td>
                      <td className={td + " text-center"}>{a.year_manufactured||'—'}</td>
                      <td className={td}>{a.region||'—'}</td>
                      <td className={td}>{a.unit||'—'}</td>
                      <td className={td}>{a.assigned_city||'—'}</td>
                      <td className={td}>{a.assigned_precinct||'—'}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.color} border ${sc.border}`}>{a.status||'—'}</span>
                      </td>
                      <td className={`px-3 py-2.5 font-medium whitespace-nowrap ${cc}`}>{a.condition||'—'}</td>
                      <td className={td}>{(a.operational_since||'').slice(0,10)||'—'}</td>
                      <td className={td + " text-center"}>{a.current_mileage ? Number(a.current_mileage).toLocaleString() : '—'}</td>
                      <td className={td + " text-center"}>{a.operational_hours||'—'}</td>
                      <td className={td}>{a.fuel_type||'—'}</td>
                      <td className={td + " text-center"}>{a.fuel_capacity_l||'—'}</td>
                      <td className={td}>{(a.last_fuel_date||'').slice(0,10)||'—'}</td>
                      <td className={td + " text-center"}>{a.last_fuel_quantity_l||'—'}</td>
                      <td className={td}>{(a.last_maintenance_date||'').slice(0,10)||'—'}</td>
                      <td className={td + " text-right"}>{a.maintenance_cost_ytd ? Number(a.maintenance_cost_ytd).toLocaleString()+' F' : '—'}</td>
                      <td className={td + " text-center"}>{a.engine_temp_warning===true||a.engine_temp_warning==='TRUE'?'⚠️':'—'}</td>
                      <td className={td + " text-center"}>{a.oil_pressure_warning===true||a.oil_pressure_warning==='TRUE'?'⚠️':'—'}</td>
                      <td className={td + " text-center"}>{a.brake_wear_pct||'—'}</td>
                      <td className={td}>{(a.last_inspection_date||'').slice(0,10)||'—'}</td>
                      <td className={td}>{(a.next_inspection_date||'').slice(0,10)||'—'}</td>
                      <td className={td}>{a.inspection_officer||'—'}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {a.inspection_result
                          ? <span className={`text-xs font-medium ${a.inspection_result==='Conforme'?'text-green-400':a.inspection_result.includes('réserves')||a.inspection_result.includes('reserves')?'text-yellow-400':'text-red-400'}`}>{a.inspection_result}</span>
                          : <span className="text-slate-500">—</span>}
                      </td>
                      <td className={`px-3 py-2.5 max-w-48 truncate text-slate-300 text-xs`} title={a.inspection_notes}>{a.inspection_notes||'—'}</td>
                      <td className={td}>{(a.repair_date||'').slice(0,10)||'—'}</td>
                      <td className={td}>{a.repair_type||'—'}</td>
                      <td className={`px-3 py-2.5 max-w-40 truncate text-slate-300 text-xs`} title={a.repair_description}>{a.repair_description||'—'}</td>
                      <td className={td + " text-right"}>{a.repair_cost ? Number(a.repair_cost).toLocaleString()+' F' : '—'}</td>
                      <td className={td}>{a.repair_technician||'—'}</td>
                      <td className={td}>{a.repair_provider||'—'}</td>
                      <td className={td + " text-center"}>{a.repair_duration_days||'—'}</td>
                      <td className={td}>{a.loss_type||'—'}</td>
                      <td className={td}>{(a.loss_date||'').slice(0,10)||'—'}</td>
                      <td className={td}>{a.loss_report_ref||'—'}</td>
                      <td className={td}>{a.decommission_reason||'—'}</td>
                      <td className={td}>{(a.decommission_date||'').slice(0,10)||'—'}</td>
                      <td className={td}>{a.decommission_approved_by||'—'}</td>
                      <td className={td}>{a.gps_latitude ? `${a.gps_latitude}, ${a.gps_longitude}` : '—'}</td>
                      <td className={`px-3 py-2.5 max-w-40 truncate text-slate-300 text-xs`} title={a.last_seen_location}>{a.last_seen_location||'—'}</td>
                      <td className={td}>{a.assigned_officer||'—'}</td>
                      <td className={td}>{a.assigned_email||'—'}</td>
                      <td className={`px-3 py-2.5 font-mono text-slate-400 text-xs whitespace-nowrap`}>{a.qr_code||'—'}</td>
                      <td className={td + " text-right"}>{a.purchase_cost ? Number(a.purchase_cost).toLocaleString()+' F' : '—'}</td>
                      <td className={td}>{(a.created_at||'').slice(0,10)||'—'}</td>
                      <td className={`px-3 py-2.5 max-w-40 truncate text-slate-300 text-xs`} title={a.asset_notes}>{a.asset_notes||'—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
            </div>
          <div className="px-3 py-2 border-t border-blue-900/40 text-xs text-slate-400">
            {filtered.length} actif(s) affiché(s) sur {assets.length} total
          </div>
        </div>
      )}
    </div>
  );
};
